import React from "react";
import {
  BsTable,
  BsBarChart,
  BsSpeedometer2,
  BsExclamationTriangle,
  BsCheckCircle,
  BsInfoCircle,
  BsFilter,
} from "react-icons/bs";
import { AgGridReact } from "ag-grid-react";
import { FilterResults } from "../filtering/components";
import HtmlContainer from "../display/HtmlContainer";
import SampleNavigator from "./SampleNavigator";
import { LoadingSpinner } from "../common";
import { variantService } from "../pages/services/variantService.js";

const MemoizedAgGrid = React.memo(AgGridReact);

const MainContentPanel = ({
  token,
  showTable,
  handleTableSwitch,
  filterMessage,
  setFilterMessage,
  filter_results,
  group_counts,
  clearFilters,
  deleteFilter,
  updateFilterLogic,
  columnDefs,
  gridRef,
  onGridReady,
  defaultColDef,
  onAdvancedFilterBuilderVisibleChanged,
  onFilterChanged,
  onColumnGroupOpened,
  refreshCache,
  sidebarVisible,
  showActiveFilters,
  toggleActiveFilters,
  onGridInitialization,
}) => {
  const handleSamplesChanged = async (sampleInfo) => {
    // sampleInfo contains updated sample range info but we fetch fresh headers from API
    // Re-initialize headers with new sample range
    if (onGridInitialization) {
      try {
        const response = await variantService.getHeaders(token);
        const result = response.data;

        // Update column definitions with new headers
        onGridInitialization(result);
      } catch (error) {
        console.error("Error updating headers:", error);
      }
    }

    // Refresh the grid cache to fetch new data with updated sample range
    if (refreshCache) {
      await refreshCache();
    }
  };

  const tabs = [
    { id: 0, title: "Variants", icon: BsTable },
    { id: 1, title: "VEP Stats", icon: BsBarChart },
    { id: 2, title: "VariantQC", icon: BsSpeedometer2 },
  ];

  return (
    <div
      className="bg-white border-top pb-4 main-content-panel"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        width: "100%",
        transition: "all 0.3s ease",
        paddingLeft: "1rem",
        paddingRight: "1rem",
        boxShadow: sidebarVisible ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
      }}
    >
      {/* Header with Tab Navigation */}
      <div className="bg-white p-3" style={{ flexShrink: 0 }}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`btn btn-sm btn-animate d-flex align-items-center gap-1 justify-content-center ${
                    showTable === tab.id
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => handleTableSwitch(tab.id)}
                  type="button"
                  role="tab"
                  aria-selected={showTable === tab.id}
                  aria-controls={`tab-panel-${tab.id}`}
                  title={tab.title}
                >
                  <IconComponent />
                  <span className="fw-medium d-none d-md-inline">
                    {tab.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active Filters Toggle - only show in Variant Table view */}
          {showTable === 0 && (
            <div className="d-flex gap-2 align-items-stretch">
              <button
                className={`btn btn-sm btn-animate d-flex align-items-center gap-1 justify-content-center ${
                  showActiveFilters
                    ? "btn-outline-success"
                    : "btn-outline-secondary"
                }`}
                onClick={toggleActiveFilters}
                type="button"
                title={
                  showActiveFilters
                    ? "Hide active filters"
                    : "Show active filters"
                }
                aria-pressed={showActiveFilters}
                style={
                  showActiveFilters
                    ? {
                        backgroundColor: "#d4edda",
                        borderColor: "#c3e6cb",
                        color: "#155724",
                      }
                    : {}
                }
              >
                <BsFilter />
                <span className="fw-medium d-none d-md-inline">Filters</span>
                {filter_results.length > 0 && (
                  <span
                    className="badge ms-1"
                    style={{
                      backgroundColor: showActiveFilters
                        ? "#198754"
                        : "#e9ecef",
                      color: showActiveFilters ? "white" : "#495057",
                      fontWeight: "600",
                      border: showActiveFilters ? "none" : "1px solid #dee2e6",
                    }}
                  >
                    {(() => {
                      // Get a meaningful default count - try grid row count if no filter results
                      let defaultCount = group_counts?.combined || 0;
                      if (defaultCount === 0 && gridRef?.current?.api) {
                        try {
                          defaultCount =
                            gridRef.current.api.getDisplayedRowCount() || 0;
                        } catch (e) {
                          // Fallback to 0 if grid API not available
                          defaultCount = 0;
                        }
                      }

                      const displayCount = defaultCount;
                      return displayCount.toLocaleString();
                    })()}
                  </span>
                )}
              </button>
              {filter_results.length > 0 && (
                <button
                  className="btn btn-sm btn-outline-danger btn-animate d-flex align-items-center justify-content-center"
                  onClick={clearFilters}
                  type="button"
                  title="Clear all filters"
                  style={{
                    backgroundColor: "#f8d7da",
                    borderColor: "#f5c6cb",
                    color: "#721c24",
                    fontSize: "0.75rem",
                    padding: "0.25rem 0.75rem",
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Variant Table Panel */}
      <div
        className={`${
          showTable === 0 ? "d-flex" : "d-none"
        } flex-column flex-grow-1`}
        role="tabpanel"
        id="tab-panel-0"
        aria-labelledby="tab-0"
        style={{ height: "100%" }}
      >
        <div className="px-3 pb-1" style={{ flexShrink: 0 }}>
          {/* Filter Message Display */}
          {filterMessage && (
            <div
              className={`alert ${
                filterMessage.type === "error"
                  ? "alert-danger"
                  : filterMessage.type === "info"
                  ? "alert-warning"
                  : filterMessage.type === "processing"
                  ? "alert-info"
                  : "alert-success"
              } alert-dismissible fade show mb-3`}
              role="alert"
              style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
            >
              <div className="d-flex align-items-center">
                {filterMessage.type === "error" ? (
                  <BsExclamationTriangle className="me-2" />
                ) : filterMessage.type === "info" ? (
                  <BsInfoCircle className="me-2" />
                ) : filterMessage.type === "processing" ? (
                  <LoadingSpinner size="1em" className="me-2" />
                ) : (
                  <BsCheckCircle className="me-2" />
                )}
                <div>
                  <strong>
                    {filterMessage.type === "error"
                      ? "Error: "
                      : filterMessage.type === "info"
                      ? ""
                      : filterMessage.type === "processing"
                      ? ""
                      : "Success: "}
                  </strong>
                  {filterMessage.message}
                </div>
              </div>
              {/* Show dismiss button for all messages except processing */}
              {filterMessage.type !== "processing" && (
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setFilterMessage(null)}
                  aria-label="Close"
                ></button>
              )}
            </div>
          )}

          {/* Filter Results - Collapsible */}
          {showActiveFilters && (
            <FilterResults
              filter_results={filter_results}
              group_counts={group_counts}
              clearFilters={clearFilters}
              deleteFilter={deleteFilter}
              updateFilterLogic={updateFilterLogic}
              token={token}
            />
          )}

          {/* Sample Navigation */}
          <SampleNavigator
            token={token}
            onSamplesChanged={handleSamplesChanged}
          />
        </div>

        {/* Data Grid - grows to fill remaining space */}
        <div
          className="ag-theme-alpine flex-grow-1"
          style={{ width: "100%", minHeight: 0 }}
        >
          <MemoizedAgGrid
            columnDefs={columnDefs}
            ref={gridRef}
            pagination={true}
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 75, 100]}
            onGridReady={onGridReady}
            rowModelType={"serverSide"}
            defaultColDef={defaultColDef}
            enableAdvancedFilter={true}
            onAdvancedFilterBuilderVisibleChanged={
              onAdvancedFilterBuilderVisibleChanged
            }
            onFilterChanged={onFilterChanged}
            onColumnGroupOpened={onColumnGroupOpened}
            tooltipInteraction={false}
          />
        </div>
      </div>

      {/* VEP Stats Panel */}
      <div
        className={`${
          showTable === 1 ? "d-flex" : "d-none"
        } flex-column flex-grow-1`}
        style={{ width: "100%", height: "100%" }}
        role="tabpanel"
        id="tab-panel-1"
        aria-labelledby="tab-1"
      >
        <div className="p-3 border-bottom bg-light" style={{ flexShrink: 0 }}>
          <h5 className="mb-0 d-flex align-items-center">
            <BsBarChart className="me-2" />
            VEP Statistics
          </h5>
          <small className="text-muted">
            Variant Effect Predictor analysis results
          </small>
        </div>
        <div
          className="p-3 flex-grow-1"
          style={{ minHeight: 0, overflow: "auto" }}
        >
          <HtmlContainer file="VEP" token={token} />
        </div>
      </div>

      {/* VariantQC Stats Panel */}
      <div
        className={`${
          showTable === 2 ? "d-flex" : "d-none"
        } flex-column flex-grow-1`}
        style={{ width: "100%", height: "100%" }}
        role="tabpanel"
        id="tab-panel-2"
        aria-labelledby="tab-2"
      >
        <div className="p-3 border-bottom bg-light" style={{ flexShrink: 0 }}>
          <h5 className="mb-0 d-flex align-items-center">
            <BsSpeedometer2 className="me-2" />
            Variant Quality Control
          </h5>
          <small className="text-muted">
            Quality assessment and validation metrics
          </small>
        </div>
        <div
          className="p-3 flex-grow-1"
          style={{ minHeight: 0, overflow: "auto" }}
        >
          <HtmlContainer file="VariantQC" token={token} />
        </div>
      </div>
    </div>
  );
};

export default MainContentPanel;
